import {type S2aAccount, S2aAccountState} from '@oc/customer'
import {SnackHandlerInstance as snackHandler} from '@oc/shell-core'
import {type User, UserManagementService} from '@oc/user-management'
import {S2aProxy, TimezoneHelper} from '@oc/utils'
import {action, computed, makeObservable, observable} from 'mobx'
import {messages} from '../Components/translations'

export const TimeZoneNotSet = 'TIMEZONE_NOT_SET'

export class OcAppBarState {
    @observable s2aProxy?: S2aProxy = undefined
    @observable s2aAccount?: S2aAccount = undefined
    @observable currentAccountId?: string = undefined
    @observable private _localTimezoneTime = ''
    @observable private _customerTimezoneTime = ''
    @observable private _customerTimezone: string | undefined = undefined
    @observable private _loggedUser?: User = undefined
    @observable private _isFileUploadDialogOpen = false
    @observable private _isEditProfileDialogOpen = false
    @observable private _userInfoLoaded = false

    @observable private timezoneHelper = new TimezoneHelper()
    private userManagementService = new UserManagementService()

    constructor() {
        makeObservable(this)
    }

    @computed
    public get customerTimezoneTitle() {
        if (this._customerTimezone === TimeZoneNotSet) {
            return messages.getText('misc', 'customerTimezoneNotSet')
        }

        return this.timezoneHelper.getTimezone(this._customerTimezone || '')?.displayName || ''
    }

    @computed
    public get localTimezoneTitle() {
        return this.timezoneHelper.getTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)?.displayName || ''
    }

    @computed
    public get localTimezoneTime() {
        return this._localTimezoneTime
    }

    public set localTimezoneTime(value) {
        this._localTimezoneTime = value
    }

    @computed
    public get customerTimezoneTime() {
        return this._customerTimezoneTime
    }

    public set customerTimezoneTime(value) {
        this._customerTimezoneTime = value
    }

    @computed
    public get customerTimezone() {
        return this._customerTimezone
    }

    public set customerTimezone(value) {
        this._customerTimezone = value
    }

    @computed
    public get isFileUploadDialogOpen() {
        return this._isFileUploadDialogOpen
    }

    @computed
    public get isEditProfileDialogOpen() {
        return this._isEditProfileDialogOpen
    }

    @computed
    public get loggedUser() {
        return this._loggedUser
    }

    @computed
    public get userInfoLoaded() {
        return this._userInfoLoaded
    }

    @computed
    public get hasCustomerContext(): boolean {
        return Boolean(this.currentAccountId)
    }

    @computed
    public get s2aAccountDeactivated(): boolean {
        return this.s2aAccount?.state === S2aAccountState.Inactive
    }

    @action.bound
    public async load(s2aProxy: S2aProxy, account: S2aAccount | undefined, username: string, accountId?: string) {
        this._userInfoLoaded = false
        this.s2aProxy = s2aProxy
        this.s2aAccount = account
        this.currentAccountId = accountId

        try {
            await this.loadUserInfo(username)
            this._userInfoLoaded = true
        } catch {
            snackHandler.error(messages.getText('appBar', 'failedLoadUser'))
        }
    }

    @action.bound
    public async loadUserInfo(username: string) {
        const user = await this.userManagementService.getUser(username)
        this._loggedUser = user
        if (this._loggedUser) {
            this.userManagementService.loadImageForUser(this._loggedUser)
        }
    }

    @action.bound
    public async saveUserInfo() {
        if (this._loggedUser) {
            this._userInfoLoaded = false

            try {
                await this.userManagementService.updateUser(this._loggedUser)
                await this.loadUserInfo(this._loggedUser.username)
                this._userInfoLoaded = true
            } catch {
                snackHandler.error(messages.getText('appBar', 'profileSaveError'))
            }
        }
    }

    @action.bound
    public async uploadProfilePicture(file: File) {
        if (this._loggedUser) {
            try {
                const pictureName = `images/users/${this._loggedUser.username}.${file.name.split('.').pop()}`
                this._loggedUser.pictureUrl = URL.createObjectURL(file)
                this._loggedUser.picture = pictureName
                await this.userManagementService.uploadImage(this._loggedUser.picture, file)
            } catch {
                snackHandler.error(messages.getText('appBar', 'profilePictureSaveError'))
            }
            await this.saveUserInfo()
        }
    }

    @action.bound
    public toggleEditProfileDialog(open: boolean) {
        this._isEditProfileDialogOpen = open
    }

    @action.bound
    public toggleFileUploadDialog(open: boolean) {
        this._isFileUploadDialogOpen = open
    }
}
